import type {
  CompanyRunResearch,
  ResearchTaskReceipts,
} from "@/admin/monitoring/company-run-research-data"
import type {
  FileEvidenceTarget,
  loadFileEvidence,
} from "@/admin/monitoring/file-evidence-data"
import { FileEvidenceView } from "@/admin/monitoring/file-evidence-view"
import { ModelCallsDetails } from "@/admin/monitoring/model-calls-details"
import { ResearchTaskReceiptChain } from "@/admin/monitoring/research-task-receipt-chain"
import { humanizeKey } from "@/admin/monitoring/run-presentation"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { useTerminalTheme } from "@/lib/terminal/use-terminal-theme"
import { sourcePlatformDisplayName } from "@/terminal/research/platform-display-names"

const descriptionByEvidenceKind: Record<FileEvidenceTarget["kind"], string> = {
  citation: "The Source Records this Brief claim cites",
  score: "The Source Records behind this Score Input",
  detection: "The Source Records behind this Detection",
  assessment: "What the Assessment recorded when it failed",
}

// Names what the sheet shows, so it reads on its own once the page is covered.
// A Research Task can arrive by deep link, so its heading comes from the cell;
// File evidence is opened by a click that already knows its title.
const sheetHeading = ({
  research,
  researchTaskId,
  openedEvidence,
}: {
  research: CompanyRunResearch
  researchTaskId: string | null
  openedEvidence: OpenedFileEvidence | null
}) => {
  if (openedEvidence)
    return {
      title: openedEvidence.title,
      description: descriptionByEvidenceKind[openedEvidence.target.kind],
    }
  const cell = research.bands
    .flatMap((band) => band.cells)
    .find((candidate) => candidate.researchTaskId === researchTaskId)
  if (!cell)
    return { title: "Research Task", description: "Research Task receipts" }
  const subjectName =
    research.subjectNameByKey[cell.subjectKey] ?? cell.subjectKey
  return {
    title: sourcePlatformDisplayName(cell.platformKey),
    description: `${humanizeKey(cell.sourceKey)} for ${subjectName}${cell.geographyLabel ? `, ${cell.geographyLabel}` : ""}`,
  }
}

export type OpenedFileEvidence = { target: FileEvidenceTarget; title: string }

export const RunEvidenceSheet = ({
  research,
  researchTaskId,
  openedEvidence,
  receipts,
  evidence,
  error,
  isLoading,
  hasRequestedModelPayloads,
  onLoadModelPayloads,
  onClose,
  onRestoreFocus,
}: {
  research: CompanyRunResearch
  researchTaskId: string | null
  openedEvidence: OpenedFileEvidence | null
  receipts: ResearchTaskReceipts | null
  evidence: Awaited<ReturnType<typeof loadFileEvidence>> | null
  error: string | null
  isLoading: boolean
  hasRequestedModelPayloads: boolean
  onLoadModelPayloads: () => void
  onClose: () => void
  onRestoreFocus: () => void
}) => {
  const { theme } = useTerminalTheme()
  const heading = sheetHeading({ research, researchTaskId, openedEvidence })
  const loaded = receipts ?? evidence
  return (
    <Sheet
      open={researchTaskId !== null || openedEvidence !== null}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose()
      }}
    >
      <SheetContent
        data-terminal-theme={theme}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          requestAnimationFrame(onRestoreFocus)
        }}
        className="monitoring-workspace bg-surface-layer text-text-primary w-full gap-0 font-mono sm:max-w-2xl"
      >
        <SheetHeader className="shrink-0 border-b pr-12">
          <SheetTitle>{heading.title}</SheetTitle>
          <SheetDescription className="text-xs">
            {heading.description}
          </SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 space-y-8 overflow-y-auto p-4">
          {error && (
            <p role="alert" className="text-assessment-critical text-sm">
              {error}
            </p>
          )}
          {!error && !loaded && (
            <p role="status" className="text-muted-foreground text-sm">
              Loading evidence…
            </p>
          )}
          {receipts && <ResearchTaskReceiptChain receipts={receipts} />}
          {evidence && (
            <FileEvidenceView
              evidence={evidence}
              isAssessment={openedEvidence?.target.kind === "assessment"}
            />
          )}
          {loaded && (
            <ModelCallsDetails
              modelCalls={loaded.modelCalls}
              modelCallLimit={loaded.modelCallLimit}
              isRequested={hasRequestedModelPayloads}
              isLoading={isLoading}
              onOpen={onLoadModelPayloads}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
